import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DireccionPersonaRepository } from 'src/app/domain/service/persona/direccion-persona-repository';
import { DireccionPersona, DireccionPersonaRequest } from 'src/app/domain/model/persona/direccion-persona';

/**
 * Adaptador HTTP: HttpDireccionPersonaRepository
 *
 * Implementa el puerto DireccionPersonaRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (DireccionPersonaController).
 *
 * Endpoints:
 * - GET /api/personas/{cdpersona}/direcciones
 * - GET /api/personas/{cdpersona}/direcciones/{id}
 * - POST /api/personas/{cdpersona}/direcciones
 * - PUT /api/personas/{cdpersona}/direcciones/{id}
 * - DELETE /api/personas/{cdpersona}/direcciones/{id}
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpDireccionPersonaRepository implements DireccionPersonaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/personas`;

  /**
   * Construye la URL para direcciones de una persona
   */
  private direccionesUrl(cdpersona: number): string {
    return `${this.baseUrl}/${cdpersona}/direcciones`;
  }

  /**
   * Obtiene todas las direcciones de una persona
   */
  getByPersona(cdpersona: number): Promise<DireccionPersona[]> {
    return firstValueFrom(
      this.http.get<DireccionPersona[]>(this.direccionesUrl(cdpersona))
    );
  }

  /**
   * Obtiene solo direcciones activas de una persona
   */
  getActivasByPersona(cdpersona: number): Promise<DireccionPersona[]> {
    const params = new HttpParams().set('activo', 'S');
    return firstValueFrom(
      this.http.get<DireccionPersona[]>(this.direccionesUrl(cdpersona), { params })
    );
  }

  /**
   * Obtiene una dirección por ID
   */
  getById(cdpersona: number, nmconsecutivo: number): Promise<DireccionPersona> {
    return firstValueFrom(
      this.http.get<DireccionPersona>(`${this.direccionesUrl(cdpersona)}/${nmconsecutivo}`)
    );
  }

  /**
   * Crea una nueva dirección
   */
  create(cdpersona: number, direccion: DireccionPersonaRequest): Promise<DireccionPersona> {
    return firstValueFrom(
      this.http.post<DireccionPersona>(this.direccionesUrl(cdpersona), direccion)
    );
  }

  /**
   * Actualiza una dirección existente
   */
  update(cdpersona: number, nmconsecutivo: number, direccion: DireccionPersonaRequest): Promise<DireccionPersona> {
    return firstValueFrom(
      this.http.put<DireccionPersona>(`${this.direccionesUrl(cdpersona)}/${nmconsecutivo}`, direccion)
    );
  }

  /**
   * Inactiva una dirección (soft delete)
   */
  delete(cdpersona: number, nmconsecutivo: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${this.direccionesUrl(cdpersona)}/${nmconsecutivo}`)
    );
  }

  /**
   * Cuenta direcciones activas de una persona
   */
  countActivas(cdpersona: number): Promise<number> {
    return firstValueFrom(
      this.http.get<number>(`${this.direccionesUrl(cdpersona)}/count`)
    );
  }
}
